// Small geo helpers for nearest-door selection and live position.

export interface LatLng {
  lat: number;
  lng: number;
}

/** Haversine distance in metres. */
export function distanceM(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Nearest item (with lat/lng) to a point. Items missing coords are skipped. */
export function nearest<T extends { lat: number | null; lng: number | null }>(
  from: LatLng | null,
  items: T[],
): T | null {
  let best: T | null = null;
  let bestD = Infinity;
  for (const it of items) {
    if (it.lat == null || it.lng == null) continue;
    if (!from) return it; // no fix yet → first available
    const d = distanceM(from, { lat: it.lat, lng: it.lng });
    if (d < bestD) {
      bestD = d;
      best = it;
    }
  }
  return best ?? items[0] ?? null;
}

/**
 * Reverse-geocode a GPS point to a short street address, as a SUGGESTION only.
 * Non-blocking, never throws (returns null offline/on failure). Uses OSM Nominatim;
 * for production swap in a proper geocoder (MapTiler/your own) — Nominatim is
 * rate-limited (~1 req/sec) and not for bulk use.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return null;
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=jsonv2&addressdetails=1&zoom=18`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const j = (await res.json()) as { display_name?: string; address?: Record<string, string> };
    const a = j.address ?? {};
    const road = a.road ?? a.pedestrian ?? a.footway;
    if (road) {
      const label = [a.house_number, road].filter(Boolean).join(' ');
      return a.city || a.town ? `${label}, ${a.city ?? a.town}` : label;
    }
    return j.display_name ? j.display_name.split(',').slice(0, 2).join(',').trim() : null;
  } catch {
    return null;
  }
}

export interface GeoResult {
  label: string;
  lat: number;
  lng: number;
}

/**
 * Forward-geocode an address/street query to candidate points (typeahead).
 * Scoped to `viewbox` [west, south, east, north] when given (the district), so
 * results stay local. Non-blocking, never throws. OSM Nominatim — placeholder.
 */
export async function geocodeSearch(query: string, viewbox?: [number, number, number, number]): Promise<GeoResult[]> {
  try {
    const q = query.trim();
    if (q.length < 3) return [];
    if (typeof navigator !== 'undefined' && !navigator.onLine) return [];
    const params = new URLSearchParams({ q, format: 'jsonv2', addressdetails: '1', limit: '6' });
    if (viewbox) {
      params.set('viewbox', `${viewbox[0]},${viewbox[1]},${viewbox[2]},${viewbox[3]}`);
      params.set('bounded', '1');
    }
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const arr = (await res.json()) as Array<{ display_name: string; lat: string; lon: string; address?: Record<string, string> }>;
    // Nominatim returns one row per street segment, so a single street can repeat
    // many times — collapse to distinct labels (first match = most relevant) so the
    // typeahead doesn't show "Menzies Street, Victoria" five times in a row.
    const seen = new Set<string>();
    const out: GeoResult[] = [];
    for (const r of arr) {
      const a = r.address ?? {};
      const road = a.road ?? a.pedestrian ?? a.footway;
      const label = road
        ? [a.house_number, road].filter(Boolean).join(' ') + (a.city || a.town ? `, ${a.city ?? a.town}` : '')
        : r.display_name.split(',').slice(0, 3).join(',');
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ label, lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
    }
    return out;
  } catch {
    return [];
  }
}

/** One-shot geolocation; resolves null if unavailable/denied (never throws). */
export function getPosition(timeoutMs = 8000): Promise<LatLng | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 15000 },
    );
  });
}

/**
 * Sort households into a rough walking order: by street, then odd/even side
 * grouped, then ascending house number. Falls back to address string order.
 */
export function walkingOrder<T extends { address: string | null }>(items: T[]): T[] {
  const parse = (addr: string | null) => {
    if (!addr) return { num: Number.POSITIVE_INFINITY, even: 0, street: '' };
    const m = addr.match(/^\s*(\d+)\s+(.*)$/);
    const num = m ? parseInt(m[1]!, 10) : Number.POSITIVE_INFINITY;
    const street = (m ? m[2]! : addr).toLowerCase();
    return { num, even: num % 2, street };
  };
  return [...items].sort((a, b) => {
    const pa = parse(a.address);
    const pb = parse(b.address);
    if (pa.street !== pb.street) return pa.street < pb.street ? -1 : 1;
    if (pa.even !== pb.even) return pa.even - pb.even; // odd side then even side
    return pa.num - pb.num;
  });
}
